import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getDataHealth, verifyCollisions, verifyMerged, matchCollisionMasters, feesLlmCheck, feesLlmApply, applyNmcCollegeType, purgeSharedAlias, resolveAiimsShortforms, resolveGmcShortforms, resolveMgmMaharashtra, resolveScbCuttack, resolveSonbhadraAsmc, resolveAcsrNellore, resolveGenericTemplateAliases, enqueueEnrichmentRun } from "../lib/api";
import { CaretLeft, CaretDown, CaretRight, Heartbeat, ArrowClockwise, CheckCircle, Warning, Info, Trash } from "@phosphor-icons/react";
import CrossWiredPanel from "../components/CrossWiredPanel";

const CHECK_META = {
  code_collisions: {
    title: "College-code collisions",
    desc: "One college_code covering 2+ different institutes (e.g. Medical + Dental, or two cities). Splits cause course/cutoff leaks.",
    severity: "high",
  },
  missing_fees: {
    title: "Colleges with no fees",
    desc: "Colleges with zero fee data (no govt / regular / NRI) for any year.",
    severity: "medium",
  },
  type_contradictions: {
    title: "Type ↔ Highlights contradictions",
    desc: "college_type disagrees with the ownership stated in highlights.",
    severity: "high",
  },
  year_mismatches: {
    title: "Established-year mismatches",
    desc: "established_year field differs from the founding year in highlights.",
    severity: "medium",
  },
  name_collisions: {
    title: "Short-name collisions",
    desc: "Similar names in a state sharing established_year/type (possible copied data).",
    severity: "low",
  },
  shared_years: {
    title: "Shared established-year clusters",
    desc: "One year set on ≥4 colleges in a state — usually normal (new govt batches).",
    severity: "low",
    kind: "info",
  },
  nmc_type_drift: {
    title: "NMC vs DB college type",
    desc: "college_type disagrees with the latest NMC seat-matrix 'management' column (govt/private only — trust/deemed skipped, NMC's sheet can't express those).",
    severity: "high",
  },
  nmc_code_collisions: {
    title: "NMC code collisions (matching bug)",
    desc: "One college_code matched by >1 DISTINCT raw NMC code in the latest ingest — each NMC code is one real institution, so this means seat data from multiple different colleges landed on one master. Fix via 'Rematch & Apply' in NMC Raw (reuses already-extracted raw rows, no PDF re-read).",
    severity: "high",
  },
  shared_aliases: {
    title: "Common/generic aliases (matcher risk)",
    desc: "An alias attached to 2+ distinct college codes (short_name is excluded — already not a matcher signal). Generic names like \"Govt Medical College\" can cause future NMC ingests to collapse unrelated colleges onto one master. Purge to remove the risk; re-run Rematch & Apply in NMC Raw afterwards.",
    severity: "high",
  },
  college_code_duplicates: {
    title: "Duplicate college_code (2 colleges, 1 code)",
    desc: "colleges_master.college_code has no unique DB index — this shows any code shared by 2 genuinely different colleges (different _id). Any lookup by this code (college page, seat-matrix/allotment matching, counselling_results aggregation) can only resolve to one of them, silently pooling or hiding the other's data.",
    severity: "high",
  },
  shared_identity_names: {
    title: "Shared name/alias across 2+ colleges (full scan)",
    desc: "Every primary_name / nmc_data.name / alias that resolves to 2+ DIFFERENT college_codes — the exact universe the Update Keys cascade's Stage 0 + key registry index over. Short generic acronyms (AIIMS, GMC) are tagged and expected — MCC's PDFs print short-forms. Everything else needs a look: could be a duplicate college, a genuine name coincidence, or a stale alias from a bad merge.",
    severity: "medium",
  },
};

const SEV_STYLE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

function Row({ children }) {
  return <div className="px-3 py-2 border-t border-zinc-100 text-sm text-[#3F3F46]">{children}</div>;
}

function CollegeLink({ code, name }) {
  return (
    <Link to={`/colleges/${code}`} className="font-semibold text-[#6043EF] hover:underline" data-testid={`dh-link-${code}`}>
      {name || code}
    </Link>
  );
}

export default function AdminDataHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState({});
  const [verifying, setVerifying] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [showVerified, setShowVerified] = useState(false);
  const [matching, setMatching] = useState(false);
  // Missing-fees LLM check → approve → apply
  const [feesChecking, setFeesChecking] = useState(false);
  const [feesApplying, setFeesApplying] = useState(false);
  const [feesProposals, setFeesProposals] = useState(null); // [{college_code,name,proposed,nri_currency}]
  const [feesApproved, setFeesApproved] = useState({}); // code -> bool
  const [feesApplyMsg, setFeesApplyMsg] = useState(null);
  // Re-fetch grounded metadata (agentic tool) for a check's colleges
  const [metaFetching, setMetaFetching] = useState(false);
  const [metaMsg, setMetaMsg] = useState(null);
  // NMC-vs-DB college type drift → apply
  const [nmcApplying, setNmcApplying] = useState(false);
  const [nmcApplyMsg, setNmcApplyMsg] = useState(null);
  // Shared/generic alias purge
  const [purgingAlias, setPurgingAlias] = useState(null); // alias_norm currently purging
  const [purgeResults, setPurgeResults] = useState([]); // [{alias_norm, alias_sample, removed}]
  // AIIMS / GMC generic-alias -> precise short-form resolve
  const [resolvingAiims, setResolvingAiims] = useState(false);
  const [aiimsResolveMsg, setAiimsResolveMsg] = useState(null);
  const [resolvingGmc, setResolvingGmc] = useState(false);
  const [gmcResolveMsg, setGmcResolveMsg] = useState(null);
  const [resolvingMgm, setResolvingMgm] = useState(false);
  const [mgmResolveMsg, setMgmResolveMsg] = useState(null);
  const [resolvingScb, setResolvingScb] = useState(false);
  const [scbResolveMsg, setScbResolveMsg] = useState(null);
  const [resolvingSonbhadra, setResolvingSonbhadra] = useState(false);
  const [sonbhadraResolveMsg, setSonbhadraResolveMsg] = useState(null);
  const [resolvingAcsr, setResolvingAcsr] = useState(false);
  const [acsrResolveMsg, setAcsrResolveMsg] = useState(null);
  const [resolvingGenericTemplates, setResolvingGenericTemplates] = useState(false);
  const [genericTemplatesResolveMsg, setGenericTemplatesResolveMsg] = useState(null);

  const load = () => {
    setLoading(true); setErr(false);
    getDataHealth()
      .then((r) => setData(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const runMatchMasters = () => {
    setMatching(true);
    matchCollisionMasters()
      .then(() => load())
      .catch(() => setErr(true))
      .finally(() => setMatching(false));
  };

  const runVerify = () => {
    setVerifying(true); setMergeResult(null);
    verifyCollisions()
      .then(() => load())
      .catch(() => setErr(true))
      .finally(() => setVerifying(false));
  };

  const runVerifyMerged = () => {
    setMerging(true);
    verifyMerged()
      .then((r) => { setMergeResult(r.data); return load(); })
      .catch(() => setErr(true))
      .finally(() => setMerging(false));
  };

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const runApplyNmcType = (codes) => {
    setNmcApplying(true); setNmcApplyMsg(null);
    applyNmcCollegeType(codes || [])
      .then((r) => { setNmcApplyMsg(`Applied NMC's govt/private classification to ${r.data.applied} college(s).`); return load(); })
      .catch(() => setErr(true))
      .finally(() => setNmcApplying(false));
  };

  const runFeesCheck = (codes) => {
    setFeesChecking(true); setFeesApplyMsg(null);
    feesLlmCheck(codes)
      .then((r) => {
        const props = (r.data.results || []).filter((x) => x.proposed && Object.keys(x.proposed).length > 0);
        setFeesProposals(props);
        setFeesApproved(Object.fromEntries(props.map((p) => [p.college_code, true])));
      })
      .catch(() => setErr(true))
      .finally(() => setFeesChecking(false));
  };

  // Re-enrich (agentic enrichment worker) for an arbitrary code list.
  // Re-grounds on each college's official website, regenerates merged
  // (official/ai-tagged) highlights + re-derives college_type/established_year.
  const runMetaFetch = (codes) => {
    if (!codes || codes.length === 0) return;
    setMetaFetching(true); setMetaMsg(null);
    enqueueEnrichmentRun(codes, "data_health_type_contradictions")
      .then((r) => {
        setMetaMsg(`Queued enrichment for ${r?.data?.count ?? codes.length} college(s). The agent re-grounds on each official website, regenerates OFFICIAL/AI-tagged highlights and re-derives college type — running in the background. Refresh Data Health in a few minutes.`);
      })
      .catch((e) => setMetaMsg(`Failed to queue: ${e?.response?.data?.detail || "error"}`))
      .finally(() => setMetaFetching(false));
  };


  const runFeesApply = () => {
    const proposals = (feesProposals || [])
      .filter((p) => feesApproved[p.college_code])
      .map((p) => ({ college_code: p.college_code, fees: p.proposed, nri_currency: p.nri_currency }));
    if (proposals.length === 0) return;
    setFeesApplying(true);
    feesLlmApply(proposals)
      .then((r) => { setFeesApplyMsg(`Applied ${r.data.applied} college(s).`); setFeesProposals(null); return load(); })
      .catch(() => setErr(true))
      .finally(() => setFeesApplying(false));
  };

  const runPurgeAlias = (aliasNorm, aliasSample, codes) => {
    setPurgingAlias(aliasNorm);
    purgeSharedAlias(aliasNorm, codes)
      .then((r) => {
        setPurgeResults((rs) => [{ alias_norm: aliasNorm, alias_sample: aliasSample, removed: r.data.removed }, ...rs]);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setPurgingAlias(null));
  };

  const runResolveAiimsShortforms = () => {
    setResolvingAiims(true); setAiimsResolveMsg(null);
    resolveAiimsShortforms()
      .then((r) => {
        setAiimsResolveMsg(`Resolved ${r.data.updated.length} AIIMS branches to precise city short-forms.`);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingAiims(false));
  };

  const runResolveGmcShortforms = () => {
    setResolvingGmc(true); setGmcResolveMsg(null);
    resolveGmcShortforms()
      .then((r) => {
        setGmcResolveMsg(`Resolved ${r.data.updated.length} GMC colleges to precise city short-forms.`);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingGmc(false));
  };

  const runResolveMgmMaharashtra = () => {
    setResolvingMgm(true); setMgmResolveMsg(null);
    resolveMgmMaharashtra()
      .then((r) => {
        const moved = r.data.steps?.find((s) => s.action === "move_panvel_allotments")?.modified ?? 0;
        setMgmResolveMsg(`Split Kamothe/Nerul/Panvel into 3 correctly-identified masters (moved ${moved} Panvel allotment rows).`);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingMgm(false));
  };

  const runResolveScbCuttack = () => {
    setResolvingScb(true); setScbResolveMsg(null);
    resolveScbCuttack()
      .then((r) => {
        setScbResolveMsg(r.data.moved
          ? `Moved NMC code OD/017/G/1 (250 MBBS seats) from the Dental college to SCB Medical College, Cuttack.`
          : r.data.reason);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingScb(false));
  };

  const runResolveSonbhadraAsmc = () => {
    setResolvingSonbhadra(true); setSonbhadraResolveMsg(null);
    resolveSonbhadraAsmc()
      .then((r) => {
        setSonbhadraResolveMsg(r.data.merged
          ? `Merged UP_ASMC_SONBHADRA into UP_MCC001 (${r.data.allotments_moved} allotment rows, ${r.data.counselling_results_moved} results moved).`
          : r.data.reason);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingSonbhadra(false));
  };

  const runResolveAcsrNellore = () => {
    setResolvingAcsr(true); setAcsrResolveMsg(null);
    resolveAcsrNellore()
      .then((r) => {
        setAcsrResolveMsg(r.data.merged
          ? `Merged 200446 into ACSR (${r.data.allotments_moved} allotment rows moved, MCC code ${r.data.mcc_code_merged || "—"} preserved).`
          : r.data.reason);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingAcsr(false));
  };

  const runResolveGenericTemplateAliases = () => {
    setResolvingGenericTemplates(true); setGenericTemplatesResolveMsg(null);
    resolveGenericTemplateAliases()
      .then((r) => {
        const totalColleges = (r.data.results || []).reduce((s, g) => s + g.modified, 0);
        setGenericTemplatesResolveMsg(`Removed the bare generic alias from ${totalColleges} college(s) across ${r.data.groups_processed} group(s).`);
        return load();
      })
      .catch(() => setErr(true))
      .finally(() => setResolvingGenericTemplates(false));
  };

  return (
    <div className="min-h-screen" data-testid="admin-data-health-page">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-end justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2 hover:underline" data-testid="dh-back-link">
              <CaretLeft size={11} weight="bold" /> Back to Admin
            </Link>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading flex items-center gap-2">
              <Heartbeat size={28} weight="bold" className="text-[#6043EF]" /> Data Health
            </h1>
            <p className="text-xs text-[#52525B] mt-1">
              Read-only deterministic checks over the college database. No LLM, no cost.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="h-10 px-5 border border-zinc-300 bg-white text-[#09090B] font-bold text-xs uppercase tracking-wider hover:border-[#6043EF] hover:text-[#6043EF] transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="dh-refresh-btn"
          >
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> {loading ? "Scanning…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-4">
        {loading && <div className="p-10 text-center text-sm text-[#52525B]" data-testid="dh-loading">Scanning {data ? data.total_colleges : ""} colleges…</div>}
        {err && <div className="p-6 text-center text-sm text-red-600 border border-red-200 bg-red-50" data-testid="dh-error">Failed to load data health report.</div>}

        {data && !loading && (
          <>
            <p className="text-xs text-[#52525B]" data-testid="dh-total">
              Scanned <span className="font-bold text-[#09090B]">{data.total_colleges}</span> colleges.
            </p>

            {/* Cross-wired masters (two MCC codes on one master) — actionable panel */}
            <CrossWiredPanel />

            {Object.entries(CHECK_META).map(([key, meta]) => {
              const check = data.checks[key] || { count: 0, items: [] };
              const clean = check.count === 0;
              const info = meta.kind === "info";
              const isOpen = !!open[key];
              const badgeStyle = info
                ? "bg-sky-50 text-sky-700 border-sky-200"
                : (clean ? "bg-emerald-50 text-emerald-700 border-emerald-200" : SEV_STYLE[meta.severity]);
              return (
                <div key={key} className="border border-zinc-200 bg-white" data-testid={`dh-check-${key}`}>
                  <button
                    onClick={() => !clean && toggle(key)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left ${clean ? "cursor-default" : "hover:bg-zinc-50"}`}
                    data-testid={`dh-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      {info
                        ? <Info size={18} weight="fill" className="text-sky-500 shrink-0" />
                        : (clean
                          ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />
                          : <Warning size={18} weight="fill" className="text-amber-500 shrink-0" />)}
                      <div>
                        <p className="text-sm font-bold text-[#09090B]">
                          {meta.title}
                          {info && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-sky-600">Data Info</span>}
                        </p>
                        <p className="text-xs text-[#52525B]">{meta.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs font-bold px-2.5 py-1 border ${badgeStyle}`} data-testid={`dh-count-${key}`}>
                        {check.count}
                      </span>
                      {!clean && (isOpen ? <CaretDown size={14} weight="bold" className="text-zinc-400" /> : <CaretRight size={14} weight="bold" className="text-zinc-400" />)}
                    </div>
                  </button>

                  {isOpen && !clean && (
                    <div className="border-t border-zinc-200" data-testid={`dh-items-${key}`}>
                      {key === "code_collisions" && (() => {
                        const verifiedItems = check.items.filter((it) => it.merge_verified);
                        const sameItems = check.items.filter((it) => it.verdict === "same" && !it.merge_verified);
                        const otherItems = check.items.filter((it) => it.verdict !== "same");
                        const renderItem = (it, i) => (
                          <Row key={`${it.counselling_body}-${it.college_code}-${i}`}>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#6043EF]/10 text-[#6043EF] border border-[#6043EF]/30">{it.counselling_body}</span>
                              <span className="font-mono text-xs font-bold text-[#09090B]">{it.college_code}</span>
                              <span className="text-xs text-red-600 font-medium">· {it.reason}</span>
                              {it.verdict && (
                                <span
                                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${it.verdict === "different" ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                                  title={it.verdict_reason || ""}
                                  data-testid={`collision-verdict-${it.college_code}`}
                                >
                                  LLM: {it.verdict}
                                </span>
                              )}
                              {it.merge_verified && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border bg-sky-50 text-sky-700 border-sky-200" data-testid={`collision-merged-${it.college_code}`}>
                                  ✓ merged
                                </span>
                              )}
                            </div>
                            {it.verdict_reason && (
                              <p className="text-[11px] text-zinc-500 italic mb-1 ml-1">{it.verdict_reason}</p>
                            )}
                            {it.master_name && (
                              <p className="text-[11px] mb-1 ml-1" data-testid={`collision-master-${it.college_code}`}>
                                <span className="font-bold text-emerald-700 uppercase tracking-wider text-[9px] mr-1">Master →</span>
                                <span className="text-zinc-700">{it.master_name}</span>
                                {it.master_match_reason && <span className="text-zinc-400 italic"> · {it.master_match_reason}</span>}
                              </p>
                            )}
                            <ul className="ml-1 space-y-0.5">
                              {it.samples.map((s, j) => {
                                const roleStyle = s.role === "master" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                                  : s.role === "reuse" ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                                  : s.role === "new" ? "bg-amber-100 text-amber-800 border-amber-300" : "";
                                const roleLabel = s.role === "master" ? "MASTER"
                                  : s.role === "reuse" ? `REUSE ${s.target_code}`
                                  : s.role === "new" ? "NEW MASTER" : "";
                                return (
                                <li key={j} className={`text-xs flex items-baseline gap-2 flex-wrap ${s.role === "master" ? "text-emerald-900" : "text-zinc-600"}`}>
                                  {roleLabel && (
                                    <span className={`inline-block font-mono text-[9px] font-bold uppercase tracking-wider px-1 whitespace-nowrap border ${roleStyle}`} data-testid={`collision-role-${it.college_code}-${j}`}>
                                      {roleLabel}
                                    </span>
                                  )}
                                  <span className="inline-block font-mono text-[10px] font-bold text-[#6043EF] whitespace-nowrap">{s.records?.toLocaleString?.() ?? s.records} recs</span>
                                  <span className="inline-block font-mono text-[10px] font-bold text-zinc-400 whitespace-nowrap">{(s.courses && s.courses.length ? s.courses.join("/") : s.course) || "?"}</span>
                                  {s.sources && s.sources.length > 0 && (
                                    <span className="inline-block font-mono text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1 whitespace-nowrap" title="Original source body (pre-merge leg)">{s.sources.join(", ")}</span>
                                  )}
                                  <span>{s.name}</span>
                                  {s.role === "reuse" && s.target_name && (
                                    <span className="text-indigo-600">→ {s.target_name}</span>
                                  )}
                                </li>
                                );
                              })}
                            </ul>
                          </Row>
                        );
                        return (
                          <>
                            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between gap-2">
                              <span className="text-[11px] text-[#52525B]">
                                Ask the LLM to classify each code as <b>same</b> college or <b>different</b> colleges.
                              </span>
                              <button
                                onClick={runVerify}
                                disabled={verifying}
                                className="h-8 px-3 bg-[#6043EF] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-[#4c34c9] transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-verify-collisions-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={verifying ? "animate-spin" : ""} />
                                {verifying ? "Verifying…" : "Verify with LLM"}
                              </button>
                            </div>

                            {/* Different / unverified — need manual split */}
                            <div className="px-3 py-2 bg-red-50/50 border-b border-red-100 flex items-center justify-between gap-2" data-testid="dh-section-different">
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-red-700">Different / unverified — need splitting ({otherItems.length})</p>
                                <p className="text-[11px] text-red-800/70">Ask the LLM to match each code's master name to the correct institute (by name + city).</p>
                              </div>
                              <button
                                onClick={runMatchMasters}
                                disabled={matching || otherItems.length === 0}
                                className="h-8 px-3 bg-red-600 text-white font-bold text-[10px] uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-match-masters-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={matching ? "animate-spin" : ""} />
                                {matching ? "Matching…" : "Match master (LLM)"}
                              </button>
                            </div>
                            {otherItems.map(renderItem)}

                            {/* Same college — merge & alias */}
                            <div className="px-3 py-2 bg-emerald-50/60 border-y border-emerald-100 flex items-center justify-between gap-2" data-testid="dh-section-same">
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Same college — merge &amp; alias ({sameItems.length})</p>
                                <p className="text-[11px] text-emerald-800/70">Confirms cutoff rows are merged under one code and saves each name as a master alias (+ mcc_code_map for MCC).</p>
                              </div>
                              <button
                                onClick={runVerifyMerged}
                                disabled={merging || sameItems.length === 0}
                                className="h-8 px-3 bg-emerald-600 text-white font-bold text-[10px] uppercase tracking-wider hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-verify-merged-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={merging ? "animate-spin" : ""} />
                                {merging ? "Merging…" : "Verify is merged"}
                              </button>
                            </div>
                            {mergeResult && (
                              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-900" data-testid="dh-merge-result">
                                Processed <b>{mergeResult.processed}</b> code(s):{" "}
                                {mergeResult.results.map((r) => (
                                  <span key={r.college_code} className="inline-block mr-3">
                                    <b>{r.college_code}</b> — {r.merged ? "✓ merged" : "⚠ not merged"} ({r.cutoff_rows} cutoff rows), +{r.aliases_added} alias{r.code_map_added ? `, +${r.code_map_added} code-map` : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                            {sameItems.map(renderItem)}

                            {/* FYI: Same & merge verified — resolved, collapsible */}
                            {verifiedItems.length > 0 && (
                              <>
                                <button
                                  onClick={() => setShowVerified((s) => !s)}
                                  className="w-full px-3 py-2 bg-sky-50/70 border-y border-sky-100 flex items-center justify-between gap-2 hover:bg-sky-100/60 transition-colors"
                                  data-testid="dh-section-verified-toggle"
                                >
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 flex items-center gap-1.5">
                                    <Info size={13} weight="fill" className="text-sky-500" />
                                    FYI: Same &amp; merge-verified ({verifiedItems.length}) — resolved
                                  </span>
                                  {showVerified ? <CaretDown size={13} weight="bold" className="text-sky-400" /> : <CaretRight size={13} weight="bold" className="text-sky-400" />}
                                </button>
                                {showVerified && verifiedItems.map(renderItem)}
                              </>
                            )}
                          </>
                        );
                      })()}

                      {key === "missing_fees" && (() => {
                        const codes = check.items.map((it) => it.college_code);
                        const nextBatch = codes.slice(0, 15);
                        return (
                          <>
                            <div className="px-3 py-2 bg-amber-50/50 border-b border-amber-100 flex items-center justify-between gap-3 flex-wrap" data-testid="dh-missing-fees-note">
                              <span className="text-[11px] text-amber-800">
                                <b>{check.count}</b>{typeof check.total_colleges === "number" ? ` / ${check.total_colleges}` : ""} colleges have
                                <b> no fee data at all</b> (no govt / regular / NRI, any year). Check with the strict LLM (exact published values only), review, then apply.
                              </span>
                              <button
                                onClick={() => runFeesCheck(nextBatch)}
                                disabled={feesChecking || feesApplying || codes.length === 0}
                                className="h-8 px-3 bg-[#6043EF] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-[#4c34c9] transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-fees-check-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={feesChecking ? "animate-spin" : ""} />
                                {feesChecking ? "Checking…" : `Check with LLM (next ${nextBatch.length})`}
                              </button>
                            </div>

                            {feesApplyMsg && (
                              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-800" data-testid="dh-fees-apply-msg">{feesApplyMsg}</div>
                            )}

                            {feesProposals && (
                              <div className="border-b border-zinc-200 bg-indigo-50/40" data-testid="dh-fees-proposals">
                                <div className="px-3 py-2 flex items-center justify-between gap-2">
                                  <span className="text-[11px] font-bold text-indigo-800">
                                    LLM found exact fees for <b>{feesProposals.length}</b> college(s). Review & approve:
                                  </span>
                                  <button
                                    onClick={runFeesApply}
                                    disabled={feesApplying || feesProposals.length === 0}
                                    className="h-8 px-3 bg-emerald-600 text-white font-bold text-[10px] uppercase tracking-wider hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                    data-testid="dh-fees-apply-btn"
                                  >
                                    <CheckCircle size={12} weight="bold" />
                                    {feesApplying ? "Applying…" : `Apply approved (${Object.values(feesApproved).filter(Boolean).length})`}
                                  </button>
                                </div>
                                {feesProposals.length === 0 && (
                                  <div className="px-3 py-2 text-[11px] text-zinc-500 italic">No exact published fees found for this batch — nothing to apply (strict integrity).</div>
                                )}
                                {feesProposals.map((p) => (
                                  <label key={p.college_code} className="flex items-start gap-2 px-3 py-1.5 border-t border-indigo-100 cursor-pointer text-sm" data-testid={`dh-fees-proposal-${p.college_code}`}>
                                    <input
                                      type="checkbox"
                                      checked={!!feesApproved[p.college_code]}
                                      onChange={(e) => setFeesApproved((a) => ({ ...a, [p.college_code]: e.target.checked }))}
                                      className="mt-1 accent-[#6043EF]"
                                    />
                                    <span>
                                      <span className="font-semibold text-[#09090B]">{p.name}</span>
                                      <span className="text-xs text-zinc-500"> — </span>
                                      {Object.entries(p.proposed).map(([k, v]) => (
                                        <span key={k} className="inline-block mr-2 text-xs font-mono text-indigo-700">
                                          {k.replace("_fees", "")}: {p.nri_currency === "USD" && k === "nri_fees" ? "$" : "₹"}{v.toLocaleString()}
                                        </span>
                                      ))}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {check.items.map((it) => (
                              <Row key={it.college_code}>
                                <CollegeLink code={it.college_code} name={it.name} />
                                <span className="text-xs text-zinc-500"> · {it.state}{it.college_type ? ` · ${it.college_type}` : ""}</span>
                              </Row>
                            ))}
                          </>
                        );
                      })()}

                      {key === "type_contradictions" && (() => {
                        const codes = check.items.map((it) => it.college_code);
                        return (
                          <>
                            <div className="px-3 py-2 bg-red-50/50 border-b border-red-100 flex items-center justify-between gap-3 flex-wrap" data-testid="dh-type-contra-note">
                              <span className="text-[11px] text-red-800">
                                <b>{check.count}</b> colleges where <b>college_type</b> disagrees with the AI highlights. Run the enrichment agent
                                — it re-grounds on each college's official website, regenerates OFFICIAL/AI-tagged highlights and re-derives college type, resolving most contradictions.
                              </span>
                              <button
                                onClick={() => runMetaFetch(codes)}
                                disabled={metaFetching || codes.length === 0}
                                className="h-8 px-3 bg-[#6043EF] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-[#4c34c9] transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-type-contra-enrich-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={metaFetching ? "animate-spin" : ""} />
                                {metaFetching ? "Queuing…" : `Enrich details (${codes.length})`}
                              </button>
                            </div>
                            {metaMsg && (
                              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-800" data-testid="dh-type-contra-meta-msg">{metaMsg}</div>
                            )}
                            {check.items.map((it) => (
                              <Row key={it.college_code}>
                                <CollegeLink code={it.college_code} name={it.name} />
                                <span className="text-xs text-zinc-500"> · {it.state} · </span>
                                <span className="text-xs text-red-600 font-medium">{it.reason}</span>
                              </Row>
                            ))}
                          </>
                        );
                      })()}

                      {key === "year_mismatches" && check.items.map((it) => (
                        <Row key={it.college_code}>
                          <CollegeLink code={it.college_code} name={it.name} />
                          <span className="text-xs text-zinc-500"> · field <b>{it.established_year}</b> vs highlights <b>{it.highlights_year}</b></span>
                        </Row>
                      ))}

                      {key === "name_collisions" && check.items.map((it, i) => (
                        <Row key={i}>
                          <span className="text-xs text-zinc-500">{it.state} · shares {it.shares}: </span>
                          <CollegeLink code={it.a.college_code} name={it.a.name} />
                          <span className="text-xs text-zinc-400"> ↔ </span>
                          <CollegeLink code={it.b.college_code} name={it.b.name} />
                        </Row>
                      ))}

                      {key === "shared_years" && check.items.map((it, i) => (
                        <Row key={i}>
                          <span className="text-xs"><b>{it.state}</b> · year <b>{it.year}</b> on <b>{it.count}</b> colleges</span>
                          <span className="text-xs text-zinc-500"> — {it.colleges.slice(0, 8).join(", ")}{it.colleges.length > 8 ? "…" : ""}</span>
                        </Row>
                      ))}

                      {key === "nmc_code_collisions" && check.items.map((it) => (
                        <Row key={it.college_code}>
                          <div data-testid={`dh-collision-${it.college_code}`}>
                            <span>
                              <CollegeLink code={it.college_code} name={it.name} /> · {it.state} ·
                              <span className="text-xs font-bold text-red-600"> {it.distinct_nmc_codes} distinct NMC codes</span> landed here
                            </span>
                            <div className="text-[11px] text-zinc-500 mt-1">
                              sr_nos: {it.rows.map((r) => r.sr_no).join(", ")} — fix via{" "}
                              <Link to="/admin/nmc-raw" className="text-[#6043EF] font-bold hover:underline">NMC Raw → Rematch &amp; Apply</Link>
                            </div>
                          </div>
                        </Row>
                      ))}

                      {key === "shared_aliases" && (() => (
                        <>
                          <div className="px-3 py-2 bg-amber-50/50 border-b border-amber-100" data-testid="dh-shared-aliases-note">
                            <span className="text-[11px] text-amber-800">
                              Deleting purges the alias text from every college listed below. This does NOT retroactively fix
                              already-matched raw rows — run <Link to="/admin/nmc-raw" className="font-bold text-[#6043EF] hover:underline">NMC Raw → Rematch &amp; Apply</Link> afterwards for the affected ingest.
                            </span>
                          </div>
                          {purgeResults.length > 0 && (
                            <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-900" data-testid="dh-purge-results">
                              {purgeResults.map((pr, i) => (
                                <div key={i}>Removed "<b>{pr.alias_sample}</b>" from <b>{pr.removed}</b> college(s).</div>
                              ))}
                            </div>
                          )}
                          {check.items.map((it) => (
                            <Row key={it.alias_norm}>
                              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5" data-testid={`dh-alias-${it.alias_norm.replace(/\s+/g, "-")}`}>
                                <span>
                                  <span className="font-mono text-xs font-bold text-[#09090B]">"{it.alias_sample}"</span>
                                  <span className="text-xs text-red-600 font-bold"> · shared by {it.count} colleges</span>
                                </span>
                                <button
                                  onClick={() => runPurgeAlias(it.alias_norm, it.alias_sample, null)}
                                  disabled={purgingAlias === it.alias_norm}
                                  className="h-7 px-3 bg-red-600 text-white font-bold text-[10px] uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                  data-testid={`dh-purge-alias-${it.alias_norm.replace(/\s+/g, "-")}`}
                                >
                                  <Trash size={12} weight="bold" />
                                  {purgingAlias === it.alias_norm ? "Purging…" : `Delete from all ${it.count}`}
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {it.colleges.map((c) => (
                                  <CollegeLink key={c.college_code} code={c.college_code} name={c.name} />
                                ))}
                              </div>
                            </Row>
                          ))}
                        </>
                      ))()}

                      {key === "college_code_duplicates" && (
                        <>
                          <div className="px-3 py-2 bg-red-50/50 border-b border-red-100" data-testid="dh-code-dupes-note">
                            <span className="text-[11px] text-red-800">
                              No fix applied here yet — review each pair (which one keeps the code, which gets a fresh one,
                              and backfill its raw_seat_matrix/raw_allotments references) before acting.
                            </span>
                          </div>
                          {check.items.map((it) => (
                            <Row key={it.college_code}>
                              <div data-testid={`dh-code-dupe-${it.college_code}`}>
                                <span className="font-mono text-xs font-bold text-red-600">{it.college_code}</span>
                                <span className="text-xs text-red-600 font-bold"> · {it.count} different colleges</span>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {it.colleges.map((c, i) => (
                                    <span key={i}>
                                      <CollegeLink code={c.college_code} name={c.name} />
                                      <span className="text-xs text-zinc-500"> ({c.state})</span>
                                      {i < it.colleges.length - 1 && <span className="text-zinc-300 mx-1">/</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </Row>
                          ))}
                        </>
                      )}

                      {key === "shared_identity_names" && (() => {
                        const generic = check.items.filter((it) => it.generic);
                        const review = check.items.filter((it) => !it.generic);
                        return (
                          <>
                            <div className="px-3 py-2 bg-amber-50/50 border-b border-amber-100 flex items-center justify-between gap-3 flex-wrap" data-testid="dh-identity-names-note">
                              <span className="text-[11px] text-amber-800">
                                Read-only scan for now — {review.length} group(s) below need a manual call (duplicate college,
                                name coincidence, or stale alias), {generic.length} are generic acronyms shown for awareness only.
                              </span>
                              <button
                                onClick={runResolveGenericTemplateAliases}
                                disabled={resolvingGenericTemplates}
                                className="h-7 px-3 bg-[#09090B] text-white font-bold text-[10px] uppercase tracking-wider hover:bg-zinc-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-resolve-generic-template-aliases"
                                title="Removes the bare generic-template alias (e.g. 'Government Medical College', 'Autonomous State Medical College') from every college in every review group below, EXCEPT the 5 small pairs that need individual investigation (mgm medical college, vydehi, rajiv gandhi institute, govt medical college badaun, netaji subhas medical college & hospital). No other field touched.">
                                <Trash size={12} weight="bold" />
                                {resolvingGenericTemplates ? "Removing…" : "Remove all generic template aliases"}
                              </button>
                            </div>
                            {genericTemplatesResolveMsg && (
                              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-900" data-testid="dh-resolve-generic-templates-msg">
                                {genericTemplatesResolveMsg}
                              </div>
                            )}
                            {review.map((it) => (
                              <Row key={it.shared_text}>
                                <div data-testid={`dh-identity-${it.shared_text.replace(/\s+/g, "-")}`}>
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span>
                                      <span className="font-mono text-xs font-bold text-[#09090B]">"{it.shared_text}"</span>
                                      <span className="text-xs text-amber-700 font-bold"> · needs review · {it.count} colleges</span>
                                    </span>
                                    {it.shared_text === "mahatma gandhi mission medical college, nerul" && (
                                      <button
                                        onClick={runResolveMgmMaharashtra}
                                        disabled={resolvingMgm}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-mgm-maharashtra"
                                        title="Splits the entangled Kamothe/Nerul/Panvel MGM branches into 3 correctly-identified masters (902807→Kamothe, MCC_MGM_NERUL→Nerul, new MCC_MGM_PANVEL), moves misfiled allotment rows, and redirects the NMC seat-matrix rows.">
                                        {resolvingMgm ? "Resolving…" : "Resolve → split Kamothe/Nerul/Panvel"}
                                      </button>
                                    )}
                                    {it.shared_text === "scb medical college, cuttack" && (
                                      <button
                                        onClick={runResolveScbCuttack}
                                        disabled={resolvingScb}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-scb-cuttack"
                                        title="Moves NMC code OD/017/G/1 (SCB Medical College, Cuttack — 250 MBBS seats) off the SCB Dental college's master (200237) onto the correct General Medicine master (MCC_SCB_MED_CUTTACK).">
                                        {resolvingScb ? "Resolving…" : "Resolve → move NMC code to Medical"}
                                      </button>
                                    )}
                                    {it.shared_text === "autonomous state medical college, sonbhadra" && (
                                      <button
                                        onClick={runResolveSonbhadraAsmc}
                                        disabled={resolvingSonbhadra}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-sonbhadra-asmc"
                                        title="Merges the UP_ASMC_SONBHADRA stub (state-counselling rows) into the canonical UP_MCC001 master (its own NMC code UP/018/G/1), then retires the stub.">
                                        {resolvingSonbhadra ? "Resolving…" : "Resolve → merge into UP_MCC001"}
                                      </button>
                                    )}
                                    {it.shared_text === "acsr government medical college, nellore" && (
                                      <button
                                        onClick={runResolveAcsrNellore}
                                        disabled={resolvingAcsr}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-acsr-nellore"
                                        title="Merges the 200446 duplicate (old placeholder NMC code + real MCC code 200668) into the canonical ACSR master (real NMC code AP/001/G/1), then retires the duplicate.">
                                        {resolvingAcsr ? "Resolving…" : "Resolve → merge into ACSR"}
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-1.5 mt-1">
                                    {it.colleges.map((c, i) => (
                                      <span key={i}>
                                        <CollegeLink code={c.college_code} name={c.name} />
                                        <span className="text-xs text-zinc-500"> ({c.state})</span>
                                        {i < it.colleges.length - 1 && <span className="text-zinc-300 mx-1">/</span>}
                                      </span>
                                    ))}
                                  </div>
                                  {it.shared_text === "mahatma gandhi mission medical college, nerul" && mgmResolveMsg && (
                                    <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-mgm-msg">
                                      {mgmResolveMsg}
                                    </div>
                                  )}
                                  {it.shared_text === "scb medical college, cuttack" && scbResolveMsg && (
                                    <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-scb-msg">
                                      {scbResolveMsg}
                                    </div>
                                  )}
                                  {it.shared_text === "autonomous state medical college, sonbhadra" && sonbhadraResolveMsg && (
                                    <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-sonbhadra-msg">
                                      {sonbhadraResolveMsg}
                                    </div>
                                  )}
                                  {it.shared_text === "acsr government medical college, nellore" && acsrResolveMsg && (
                                    <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-acsr-msg">
                                      {acsrResolveMsg}
                                    </div>
                                  )}
                                </div>
                              </Row>
                            ))}
                            {generic.length > 0 && (
                              <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-100" data-testid="dh-identity-generic-section">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Generic acronyms (expected — resolvable per case)</div>
                                {generic.map((it) => (
                                  <div key={it.shared_text} className="mb-1.5 flex items-center justify-between gap-2" data-testid={`dh-identity-generic-${it.shared_text}`}>
                                    <div>
                                      <span className="font-mono text-xs font-bold text-zinc-500">"{it.shared_text}"</span>
                                      <span className="text-xs text-zinc-400"> · {it.count} colleges: </span>
                                      <span className="text-xs text-zinc-500">
                                        {it.colleges.map((c) => c.college_code).join(", ")}
                                      </span>
                                    </div>
                                    {it.shared_text === "aiims" && (
                                      <button
                                        onClick={runResolveAiimsShortforms}
                                        disabled={resolvingAiims}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-aiims-shortforms"
                                        title="Removes the ambiguous bare AIIMS alias from all 12 branches and adds the precise city-qualified short-form MCC's own raw sheets use (e.g. AIIMS, New Delhi / AIIMS, Patna), then rebuilds the key registry.">
                                        {resolvingAiims ? "Resolving…" : "Resolve → city short-forms"}
                                      </button>
                                    )}
                                    {it.shared_text === "gmc" && (
                                      <button
                                        onClick={runResolveGmcShortforms}
                                        disabled={resolvingGmc}
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-[#6043EF] text-white hover:bg-[#4f37c9] transition-colors disabled:opacity-50"
                                        data-testid="dh-resolve-gmc-shortforms"
                                        title="Removes the ambiguous bare GMC alias from all 4 colleges and adds the precise city-qualified short-form MCC's own raw sheets use, then rebuilds the key registry.">
                                        {resolvingGmc ? "Resolving…" : "Resolve → city short-forms"}
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {aiimsResolveMsg && (
                                  <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-aiims-msg">
                                    {aiimsResolveMsg}
                                  </div>
                                )}
                                {gmcResolveMsg && (
                                  <div className="mt-1.5 text-[11px] text-emerald-700 font-medium" data-testid="dh-resolve-gmc-msg">
                                    {gmcResolveMsg}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {key === "nmc_type_drift" && (() => {
                        const codes = check.items.map((it) => it.college_code);
                        return (
                          <>
                            <div className="px-3 py-2 bg-red-50/50 border-b border-red-100 flex items-center justify-between gap-3 flex-wrap" data-testid="dh-nmc-drift-note">
                              <span className="text-[11px] text-red-800">
                                NMC is authoritative for govt/private (from the {check.year} seat-matrix ingest). Applying stamps
                                <b> field_sources.college_type="nmc_seat_matrix"</b> and never touches trust/deemed colleges.
                              </span>
                              <button
                                onClick={() => runApplyNmcType(codes)}
                                disabled={nmcApplying || codes.length === 0}
                                className="h-8 px-3 bg-red-600 text-white font-bold text-[10px] uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                                data-testid="dh-nmc-apply-all-btn"
                              >
                                <ArrowClockwise size={12} weight="bold" className={nmcApplying ? "animate-spin" : ""} />
                                {nmcApplying ? "Applying…" : `Apply NMC to all (${codes.length})`}
                              </button>
                            </div>
                            {nmcApplyMsg && (
                              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-800" data-testid="dh-nmc-apply-msg">{nmcApplyMsg}</div>
                            )}
                            {check.items.map((it) => (
                              <Row key={it.college_code}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span>
                                    <CollegeLink code={it.college_code} name={it.name} />
                                    <span className="text-xs text-zinc-500"> · {it.state} · NMC says </span>
                                    <span className="text-xs font-bold text-red-600 uppercase">{it.nmc_type}</span>
                                    <span className="text-xs text-zinc-500"> · DB has </span>
                                    <span className="text-xs font-bold text-zinc-700 uppercase">{it.db_college_type}</span>
                                  </span>
                                  <button
                                    onClick={() => runApplyNmcType([it.college_code])}
                                    disabled={nmcApplying}
                                    className="h-6 px-2 border border-red-300 text-red-600 font-bold text-[9px] uppercase tracking-wider hover:bg-red-50 transition-colors disabled:opacity-50 shrink-0"
                                    data-testid={`dh-nmc-apply-${it.college_code}`}
                                  >
                                    Apply NMC
                                  </button>
                                </div>
                              </Row>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
